import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';
import CategoryPage from './CategoryPage';

function BreakfastCategory() {
  const { city_name, vehicle_type } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();
  const { content: cms } = usePageContent('cuisine_breakfast');

  const MainText = (
    <>
      <h1>{cms?.main?.heading || `Breakfast food ${vehicleLowerCase}s in ${city_name}`}</h1>
      <CmsRichText
        html={cms?.main?.body}
        fallback={
          <p>Looking for breakfast tacos, an egg sandwich, biscuits, or a good coffee? Browse { city_name } breakfast { vehicleLowerCase }s, check menus and schedules, or request catering for a morning event.</p>
        }
      />
    </>
  );

  const SecondaryText = (
    <>
      <h3 className='u-phosphate'>{cms?.secondary?.heading || 'What you’ll find'}</h3>
      <CmsRichText
        html={cms?.secondary?.body}
        fallback={
          <p>Breakfast { vehicleLowerCase }s run short menus that move fast: breakfast tacos and burritos, egg sandwiches, biscuits, pastries, and coffee. Most open early and wrap up by late morning, so check the schedule before you head out. A few serve breakfast all day.</p>
        }
      />

      { city_name === 'Seattle' && !cms &&
        <>
          <h3 className='u-phosphate u-mt4'>Seattle’s Top Breakfast Food Trucks</h3>
          <p>Seattle’s breakfast food truck scene is packed with variety, offering everything from gourmet breakfast sandwiches to Tex-Mex morning feasts and craft coffee experiences. If you’re all about breakfast sandwiches with a gourmet twist, <Link to='wich-came-first'>’Wich Came First</Link> is a must-visit. Their Hootenanny ’Wich combines bacon, sharp cheddar, Dijon aioli, and an over-medium egg on grilled French bread. The Don’t Resist It Brisket ’Wich takes things up a notch with merlot-braised brisket, caramelized onions, and a fried egg on grilled ciabatta. The real standout, though, is their Hand Brown, a crispy, cheesy stuffed hash brown that fits perfectly in your hand for easy, on-the-go eating.</p>
          <p>For those who believe breakfast shouldn’t have a curfew, <Link to='breakfast-all-day'>Breakfast All Day</Link> delivers classic diner favorites with a street food twist. Their Chicken Fried Steak Sandwich is a southern-inspired dream, while their Scotch Egg, a soft-boiled egg wrapped in sausage and seasoned breading, is the ultimate handheld breakfast bite. Their breakfast potatoes, seasoned with a unique coffee spice blend, are a must-try, especially when loaded up with extra toppings.</p>
          <p>If bold Tex-Mex flavors are what you’re after, <Link to='alebrijes-tex-mex-breakfast'>Alebrijes Tex-Mex Breakfast</Link> is the truck to find. Their stuffed breakfast burritos, tamale plates, and their famous Carne Guisada Plate — slow-cooked pork in seasoned gravy served with tortillas, rice, and salsa — are some of the best in town. The El Sasquatcho Burrito, packed with pork in seasoned gravy, rice, and beans, is a Seattle favorite for good reason.</p>
          <p>For those who crave breakfast tacos done right, <Link to='good-morning-tacos'>Good Morning Tacos</Link> brings Austin-style breakfast perfection to Seattle. Their Smoked Brisket Breakfast Taco delivers the perfect balance of savory, spicy, and smoky flavors. Their Migas Breakfast Taco, featuring eggs scrambled with tortilla chips, tomatoes, and onion, topped with fresh avocado and salsa roja, is a morning game-changer. Their Breakfast Totchos, crispy tater tots loaded with bacon, chorizo, pickled fresnos, ranchero barbecue sauce, a sunny-up egg, and Fritos dust, are ridiculously good and absolutely addictive.</p>
          <p>No breakfast roundup would be complete without a top-tier coffee truck. <Link to='pilgrim-coffee-truck'>Pilgrim Coffee Truck</Link> serves expertly crafted espresso drinks from a beautifully restored 1945 Ford truck, making them a must-visit for coffee lovers. Their Nitro Cold Brew is ultra-smooth, and their mochas, cappuccinos, and lattes use locally roasted beans for the perfect Seattle coffee experience. Pair your coffee with one of their fresh-baked pastries, and your morning is set.</p>
        </>
      }

      <h3 className='u-phosphate u-mt4'>{cms?.events?.heading || 'Breakfast catering for your event'}</h3>
      <CmsRichText
        html={cms?.events?.body}
        fallback={
          <p>Morning meetings and early events are the usual ask: handhelds serve fast, and a coffee { vehicleLowerCase } can share the lot. Tell us your date, location, and guest count. We’ll find { vehicleLowerCase }s that fit.</p>
        }
      />
    </>
  );

  return (
    <CategoryPage
      foodName='Breakfast'
      foodCategory='breakfast'
      seoTitle={cms?.meta?.title || `Breakfast food ${vehicleLowerCase}s in ${city_name}`}
      seoDescription={cms?.meta?.description || `Find and book breakfast food ${vehicleLowerCase}s in ${city_name}. Browse menus, compare quotes, and hire breakfast catering for your next event.`}
      seoCanonical='/breakfast-food-trucks'
      MainText={MainText}
      SecondaryText={SecondaryText}
    />
  );
}

export default BreakfastCategory;
